import classNames from 'classnames';
import MLButton from '../../MLButton/MLButton';
import s from './ExportDialog.module.css';
import { useExportDialog } from './useExportDialog';
import MLLoading from '../../MLLoading/MLLoading';
import MLCheckBox from '../../MLCheckBox/MLCheckBox';
import MLRadioButton from '../../MLRadio/MLRadioButton';
import MLRadioButtonGroup from '../../MLRadio/MLRadioButtonGroup';
import {
  EXPORT_EACH_STUDENT_OPTIONS,
  EXPORT_GRADE_FORMAT_OPTIONS,
  EXPORT_OPTIONS,
} from '../../../utils/constants';
import MLFullScreenDialog from '../../MLFullScreenDialog/MLFullScreenDialog';
import RosterSearchAutocomplete from '../../RosterSearchAutocomplete/RosterSearchAutocomplete';
import { useExceptionGroupsQuery } from '../../../core/lms';
import { getConfigObject } from '../../../utils/config';

export const ExportDialogModal = ({
  isLoading,
  exportOptions,
  handleExportOptions,
  onClose,
  students = [],
  groups = [],
}) => {
  const toggleIncTimeSpent = () => {
    handleExportOptions({
      INCLUDE_TIME_SPENT: !exportOptions.INCLUDE_TIME_SPENT,
    });
  };

  const toggleIncColAvg = () => {
    handleExportOptions({ INCLUDE_COL_AVG: !exportOptions.INCLUDE_COL_AVG });
  };

  const toggleIncStudentId = () => {
    handleExportOptions({
      INCLUDE_STUDENT_ID: !exportOptions.INCLUDE_STUDENT_ID,
    });
  };

  const isDisabled =
    exportOptions.EACH_STUDENT ===
    EXPORT_EACH_STUDENT_OPTIONS.COURSE_AND_CATEGORY;

  const { FF_EXPORT_TIME_SPENT = false } = getConfigObject();

  if (isLoading) {
    return (
      <div className={s.loadingText} data-test-id="export_modal_loading">
        <MLLoading />
        <div>We're exporting your Gradebook!</div>
        <div>This could take a few minutes to complete.</div>
        <div className={s.cancelButtonContainer}>
          <MLButton
            className={classNames(s.cancelExportButton, s.modalActionButton)}
            btnType="secondaryRed"
            onClick={onClose}
          >
            Cancel Export
          </MLButton>
        </div>
      </div>
    );
  }

  const onSelect = (value) => {
    handleExportOptions({ selectedStudents: value });
  };

  const exportGradesRadioGroupId = 'export_grades_radio_group_label';
  const exportAsRadioGroupId = 'export_as_radio_group_label';

  return (
    <div data-test-id="export_gradebook_modal">
      <h6 className={s.exportTitle}>Choose what you want to export</h6>
      <div className={s.exportSubTitle}>Grades will be exported as a CSV.</div>
      <RosterSearchAutocomplete
        searchInputLabel="Students or groups included"
        groups={groups}
        students={students}
        onSelect={onSelect}
      />
      <div className={s.optionsContainer}>
        <b className={s.subTitle} id={exportGradesRadioGroupId}>
          Grades Included
        </b>
        <MLRadioButtonGroup
          labelId={exportGradesRadioGroupId}
          className={s.gradeBookSettingsGroup}
          defaultSelected={EXPORT_OPTIONS.EACH_STUDENT}
          selectedValue={exportOptions.EACH_STUDENT}
          onChange={(_e, type) => {
            handleExportOptions({
              EACH_STUDENT: type,
              ...(type === EXPORT_EACH_STUDENT_OPTIONS.COURSE_AND_CATEGORY && {
                INCLUDE_TIME_SPENT: false,
                INCLUDE_COL_AVG: false,
              }),
            });
          }}
        >
          <MLRadioButton
            value={EXPORT_EACH_STUDENT_OPTIONS.ALL_GRADES}
            name="exportGradesIncluded"
            className={s.settingsRadioBtn}
            text="All grades"
            dataId="gb_export_all_grades_radiobtn"
            testId="gb_export_all_grades_radiobtn"
          />
          <MLRadioButton
            value={EXPORT_EACH_STUDENT_OPTIONS.COURSE_AND_CATEGORY}
            name="exportGradesIncluded"
            text="Course and category totals only"
            className={s.settingsRadioBtn}
            dataId="gb_export_totals_only_radiobtn"
            testId="gb_export_totals_only_radiobtn"
          />
        </MLRadioButtonGroup>

        <b className={s.subTitle} id={exportAsRadioGroupId}>
          Export grades as
        </b>
        <MLRadioButtonGroup
          labelId={exportAsRadioGroupId}
          className={s.gradeBookSettingsGroup}
          defaultSelected={EXPORT_OPTIONS.GRADE_FORMAT}
          selectedValue={exportOptions.GRADE_FORMAT}
          onChange={(_e, type) => {
            handleExportOptions({ GRADE_FORMAT: type });
          }}
        >
          <MLRadioButton
            value={EXPORT_GRADE_FORMAT_OPTIONS.POINTS}
            text="Points"
            name="exportGradesAs"
            className={s.settingsRadioBtn}
            testId="radio_button_format_points"
          />
          <MLRadioButton
            value={EXPORT_GRADE_FORMAT_OPTIONS.PERCENTAGES}
            text="Percentages"
            name="exportGradesAs"
            className={s.settingsRadioBtn}
            testId="radio_button_format_percentages"
          />
        </MLRadioButtonGroup>

        <b className={s.subTitle}>Additional rows or columns</b>
        <div className={s.gradeBookSettingsGroup}>
          {FF_EXPORT_TIME_SPENT && (
            <div>
              <MLCheckBox
                className={s.checkboxOption}
                disabled={isDisabled}
                labelText={
                  <span className={isDisabled && s.disabledText}>
                    Time Spent columns
                  </span>
                }
                checked={exportOptions.INCLUDE_TIME_SPENT}
                onChange={toggleIncTimeSpent}
                testId="checkbox_include_column_time_spent"
                idLabel="export-activity-time-spent"
              />
              <p className={s.checkBoxSubTitle}>
                Only available for Assessment, Guided Reading, or LearningCurve
                (Adaptive Quiz) activities. Adds a column to the export with the
                time spent per student when available. Time spent provided in
                minutes. Not available for category totals.
              </p>
            </div>
          )}
          <div>
            <MLCheckBox
              className={s.checkboxOption}
              disabled={isDisabled}
              labelText={
                <span className={isDisabled && s.disabledText}>
                  Activity average row
                </span>
              }
              checked={exportOptions.INCLUDE_COL_AVG}
              onChange={toggleIncColAvg}
              testId="checkbox_include_column_average_row"
              idLabel="export-activity-average-row"
            />
            <p className={s.checkBoxSubTitle}>
              Includes a row to display the class average for included
              activities. Not available for category totals.
            </p>
          </div>
          <MLCheckBox
            className={`${s.checkboxOption}`}
            labelText={<span>Student IDs</span>}
            checked={exportOptions.INCLUDE_STUDENT_ID}
            onChange={toggleIncStudentId}
            testId="checkbox_include_student_ids"
            idLabel="export-student-ids"
          />
        </div>
      </div>
    </div>
  );
};

const ExportDialog = ({ courseShortName, courseId }) => {
  const {
    isLoading,
    toggleModal,
    handleExportGradebook,
    isShowing,
    exportOptions,
    handleExportOptions,
    roster,
  } = useExportDialog({ courseShortName });

  const { data: groups } = useExceptionGroupsQuery(courseId);

  const selectedStudentsIsEmpty = exportOptions.selectedStudents.length === 0;

  return (
    <MLFullScreenDialog
      open={isShowing}
      onClose={toggleModal}
      title="Gradebook Export"
      actions={
        <div className={s.modalActions}>
          <MLButton
            btnType="secondary"
            onClick={toggleModal}
            className={s.modalActionButton}
            disabled={isLoading}
            testId="cancel-export-button"
          >
            Cancel
          </MLButton>

          <MLButton
            btnType="primary"
            onClick={handleExportGradebook}
            className={s.modalActionButton}
            disabled={isLoading || selectedStudentsIsEmpty}
            testId="class_gb_export_button"
          >
            Export
          </MLButton>
        </div>
      }
    >
      <div className={s.exportModalContent}>
        <div className={s.exportCard}>
          <ExportDialogModal
            students={roster}
            groups={groups}
            onClose={toggleModal}
            exportOptions={exportOptions}
            handleExportOptions={handleExportOptions}
            isLoading={isLoading}
          />
        </div>
      </div>
    </MLFullScreenDialog>
  );
};

export default ExportDialog;
